import React, { useState } from 'react';
import { Button, DatePicker, Dropdown, Grid, Radio, Row, Col, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import moment from 'moment';
import { ButtonText } from '../../../../shared/globalStyling/styledText';
import { DATE_FORMAT } from '../../../../../core/components/advertiser/buildDateFilter';

const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const MODES = [
  { key: 'start', text: 'Starts', label: 'Starts', hint: 'Funnel start date is between' },
  { key: 'end', text: 'Ends', label: 'Ends', hint: 'Funnel end date is between' },
  {
    key: 'active',
    text: 'Active During',
    label: 'Active',
    hint: 'Funnel is running at any point between',
  },
];

const panelStyle = {
  width: 340,
  padding: 14,
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
};

const findMode = (key) => MODES.find((m) => m.key === key);

const toRange = (value) =>
  value && value.from && value.to
    ? [moment(value.from, DATE_FORMAT), moment(value.to, DATE_FORMAT)]
    : null;

export const formatDateFilterLabel = (value) => {
  const mode = value && findMode(value.mode);
  if (!mode || !value.from || !value.to) return 'Dates';
  const short = (date) => moment(date, DATE_FORMAT).format('MM/DD');
  return `${mode.label} ${short(value.from)}–${short(value.to)}`;
};

const DateFilterDropdown = ({ value, onApply, onClear }) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs || screens.sm;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState((value && value.mode) || 'end');
  const [range, setRange] = useState(() => toRange(value));

  const handleVisibleChange = (visible) => {
    if (visible) {
      setMode((value && value.mode) || 'end');
      setRange(toRange(value));
    }
    setOpen(visible);
  };

  const handleApply = () => {
    if (!range || !range[0] || !range[1]) return;
    onApply({ mode, from: range[0].format(DATE_FORMAT), to: range[1].format(DATE_FORMAT) });
    setOpen(false);
  };

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  const panel = (
    <div style={panelStyle}>
      <Radio.Group
        optionType="button"
        buttonStyle="solid"
        size="small"
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        style={{ display: 'flex', marginBottom: 12 }}
        options={MODES.map((m) => ({ value: m.key, label: m.text }))}
      />
      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
        {findMode(mode).hint}
      </Typography.Text>
      <RangePicker
        style={{ width: '100%', marginBottom: 12 }}
        format="MM/DD/YYYY"
        placeholder={['From', 'To']}
        value={range}
        onChange={setRange}
        getPopupContainer={(trigger) => trigger.parentNode}
      />
      <Row justify="end" gutter={8}>
        <Col>
          <Button size="small" onClick={handleClear}>
            Clear
          </Button>
        </Col>
        <Col>
          <Button size="small" type="primary" onClick={handleApply}>
            Apply
          </Button>
        </Col>
      </Row>
    </div>
  );

  const isActive = !!(value && value.mode && value.from && value.to);

  return (
    <Dropdown
      trigger={['click']}
      dropdownRender={() => panel}
      open={open}
      onOpenChange={handleVisibleChange}
    >
      <Button
        style={{
          fontSize: isMobile ? '12px' : '14px',
          width: '100%',
          height: isMobile ? '32px' : '36px',
          padding: isMobile ? '4px 8px' : undefined,
        }}
        shape="round"
        type={isActive ? 'primary' : 'default'}
        ghost={isActive}
      >
        <ButtonText text={formatDateFilterLabel(value)} />
        <DownOutlined color="black" />
      </Button>
    </Dropdown>
  );
};

export default DateFilterDropdown;
